import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { settingsActions } from "../redux/settings/slice";
import { IReduxSate } from "../typing/declarations";
import useAuth from "./useAuth";

// Paths that should never be persisted as a post-login redirect target.
// /login is excluded so we don't bounce users back to the login screen after auth.
// /not-found is excluded because the catch-all route lands here for any unknown path
// while logged out, and persisting it would later strand logged-in users on /not-found
// when they trigger any flow that respects redirectPath (e.g. yard switching).
const escapeRoute = (pathname: string) => {
  const escapeRoutes = ["/login", "/not-found"];
  return escapeRoutes.some((item) => pathname.includes(item));
};

const extractNextLink = (str: string) => {
  const params = new URLSearchParams(str);
  const next = params.get("next");

  if (!next) return "";
  return next;
};

const selectRedirectPath = (state: IReduxSate) => {
  return state.settings?.redirectPath;
};

const useRedirectPath = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { isLoggedIn } = useAuth();
  const redirectPath = useSelector(selectRedirectPath);
  const next = extractNextLink(location.search);

  const clearRedirectPath = () => {
    dispatch(settingsActions.update({ redirectPath: "" }));
  };

  useEffect(() => {
    if (!isLoggedIn && !escapeRoute(location.pathname)) {
      dispatch(settingsActions.update({ redirectPath: location.pathname }));
    }
  }, [location]);

  // Defensive: existing users may have "/not-found" stored in persisted Redux from
  // before the escapeRoute fix landed. Treat any escape-route value as no redirect.
  const safe = (path: string | undefined) => (path && !escapeRoute(path) ? path : "");

  return {
    clearRedirectPath,
    redirectPath: safe(next) || safe(redirectPath) || "/dashboard",
  };
};

export default useRedirectPath;
